import { useState, useEffect, useCallback, useRef } from "react";
import { sendAuthenticatedAsyncRequest } from "services/AsyncRequestService";
import { showNotification } from "services/NotificationService";
import {
  ROW_STATUS,
  ROW_STATUS_KEY,
  readCache,
  writeCache,
  sumColumn,
} from "./AggregatedSummaryList.utils";

const requestAsPromise = (url, method = "GET") =>
  new Promise((resolve, reject) => {
    sendAuthenticatedAsyncRequest(url, method, null, resolve, reject);
  });

const buildInitialManifestRows = (manifestRows = []) =>
  manifestRows.map((rowDef) => ({
    rowId: rowDef.rowId,
    ...rowDef.staticData,
    [ROW_STATUS_KEY]: ROW_STATUS.LOADING,
  }));

const applyRowUpdates = (rows, updates = []) => {
  const updateByRowId = updates.reduce((acc, update) => {
    acc[update.rowId] = update;
    return acc;
  }, {});

  return rows.map((row) => {
    const update = updateByRowId[row.rowId];
    if (!update) {
      return row;
    }
    return {
      ...row,
      ...update.data,
      [ROW_STATUS_KEY]: update.status || ROW_STATUS.SUCCESS,
      __error: update.error,
    };
  });
};

const runWithConcurrency = async (tasks, maxConcurrent = 4) => {
  if (!tasks.length) {
    return;
  }
  const limit = Math.max(1, maxConcurrent);
  let index = 0;

  const worker = async () => {
    while (index < tasks.length) {
      const currentIndex = index;
      index += 1;
      await tasks[currentIndex]();
    }
  };

  await Promise.all(Array.from({ length: Math.min(limit, tasks.length) }, worker));
};

export const useAggregatedSummaryList = (config) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const isMountedRef = useRef(true);

  const cacheEnabled = Boolean(config.cache?.enabled);
  const storageKey = config.cache?.storageKey;

  const showLoadError = useCallback(() => {
    const errorNotification = config.notifications?.loadError;
    showNotification({
      title: errorNotification?.title || "Unable to load data!",
      message: errorNotification?.message || "SOMETHING WENT WRONG",
      position: "tr",
      type: "error",
    });
  }, [config.notifications]);

  const persistCache = useCallback(
    (data) => {
      if (cacheEnabled && storageKey) {
        writeCache(storageKey, data);
      }
    },
    [cacheEnabled, storageKey]
  );

  const fetchApiDriven = useCallback(
    async (forceRefresh = false) => {
      const { fetch: fetchConfig } = config;
      if (!fetchConfig?.url) {
        return;
      }

      setLoading(true);
      let url = fetchConfig.url;
      if (forceRefresh && fetchConfig.forceRefreshQueryParam) {
        url += url.includes("?") ? "&" : "?";
        url += fetchConfig.forceRefreshQueryParam;
      }

      try {
        const response = await requestAsPromise(url, fetchConfig.method || "GET");
        if (!isMountedRef.current) {
          return;
        }
        const mapped = fetchConfig.mapResponse(response);
        const nextRows = mapped?.rows || [];
        setRows(nextRows);
        setLoading(false);
        persistCache({ rows: nextRows });
      } catch (error) {
        if (!isMountedRef.current) {
          return;
        }
        setLoading(false);
        showLoadError();
      }
    },
    [config, persistCache, showLoadError]
  );

  const fetchManifestRow = useCallback(async (rowDef) => {
    const fetchConfig = rowDef.fetch;
    if (!fetchConfig?.url) {
      return { rowId: rowDef.rowId, data: {}, status: ROW_STATUS.SUCCESS };
    }

    try {
      const response = await requestAsPromise(fetchConfig.url, fetchConfig.method || "GET");
      const data = fetchConfig.mapResponse ? fetchConfig.mapResponse(response, rowDef) : response?.data || {};
      return { rowId: rowDef.rowId, data, status: ROW_STATUS.SUCCESS };
    } catch (error) {
      return { rowId: rowDef.rowId, data: {}, status: ROW_STATUS.ERROR, error };
    }
  }, []);

  const fetchManifestSource = useCallback(
    async (source) => {
      try {
        const response = await requestAsPromise(source.url, source.method || "GET");
        return source.mapToRowUpdates(response);
      } catch (error) {
        if (typeof source.onError === "function") {
          return source.onError(error);
        }
        return (config.manifest?.rows || [])
          .filter((row) => row.sourceId === source.id)
          .map((row) => ({ rowId: row.rowId, data: {}, status: ROW_STATUS.ERROR, error }));
      }
    },
    [config.manifest]
  );

  const fetchManifestDriven = useCallback(
    async () => {
      const manifest = config.manifest;
      if (!manifest) {
        return;
      }

      const manifestRows = manifest.rows || [];
      const initialRows = buildInitialManifestRows(manifestRows);
      setRows(initialRows);
      setLoading(false);

      const rowUpdates = [];

      const perRowDefs = manifestRows.filter((rowDef) => rowDef.fetch?.url);
      const sourceDefs = manifest.sources || [];
      const maxConcurrent = manifest.maxConcurrent || 4;

      const perRowTasks = perRowDefs.map((rowDef) => async () => {
        const update = await fetchManifestRow(rowDef);
        rowUpdates.push(update);
        setRows((currentRows) => applyRowUpdates(currentRows, [update]));
      });

      const sourceTasks = sourceDefs.map((source) => async () => {
        const updates = await fetchManifestSource(source);
        rowUpdates.push(...updates);
        setRows((currentRows) => applyRowUpdates(currentRows, updates));
      });

      await runWithConcurrency([...sourceTasks, ...perRowTasks], maxConcurrent);

      if (isMountedRef.current) {
        persistCache({ rows: applyRowUpdates(initialRows, rowUpdates) });
      }
    },
    [config.manifest, fetchManifestRow, fetchManifestSource, persistCache]
  );

  const initialize = useCallback(
    async (forceRefresh = false) => {
      if (config.mode === "api-driven") {
        if (!forceRefresh && cacheEnabled && storageKey) {
          const cached = readCache(storageKey);
          if (cached?.rows) {
            setRows(cached.rows);
            setLoading(false);
            return;
          }
        }
        await fetchApiDriven(forceRefresh);
        return;
      }

      if (config.mode === "manifest-driven") {
        if (!forceRefresh && cacheEnabled && storageKey) {
          const cached = readCache(storageKey);
          if (cached?.rows?.length) {
            setRows(cached.rows);
            setLoading(false);
            return;
          }
        }
        await fetchManifestDriven();
      }
    },
    [cacheEnabled, config.mode, fetchApiDriven, fetchManifestDriven, storageKey]
  );

  const refresh = useCallback(() => {
    if (!cacheEnabled) {
      return;
    }
    initialize(true);
  }, [cacheEnabled, initialize]);

  useEffect(() => {
    isMountedRef.current = true;
    initialize(false);
    return () => {
      isMountedRef.current = false;
    };
  }, [initialize]);

  const totalCount =
    typeof config.header?.totalCountColumn === "string"
      ? sumColumn(rows, config.header.totalCountColumn)
      : null;

  return {
    rows,
    loading,
    totalCount,
    refresh: cacheEnabled ? refresh : undefined,
    showRefresh: cacheEnabled,
  };
};

export default useAggregatedSummaryList;
